import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAnalyticsStats, getSnapshots } from '../../../api';
import { ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
const CompanyDetails = () => {
  const navigate = useNavigate();
  const {
    companyName
  } = useParams();
  const decodedCompany = decodeURIComponent(companyName || '');
  const [data, setData] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState(decodedCompany);
  const [filterState, setFilterState] = useState('All states');
  const [filterStatus, setFilterStatus] = useState('All statuses');
  const [filterType, setFilterType] = useState('All types');
  const [mapView, setMapView] = useState('open'); // 'total', 'open', 'changes', 'objections'

  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [openFilter, setOpenFilter] = useState(null); // 'state', 'status', 'type', 'rows'
  const filterClickCount = React.useRef(0);
  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const [statsRes, snapshotsRes] = await Promise.all([getAnalyticsStats({
          search: searchTerm
        }), getSnapshots({
          page: currentPage,
          limit: rowsPerPage,
          search: searchTerm,
          state: filterState,
          status: filterStatus,
          filingType: filterType
        })]);
        if (!data) setData(statsRes.data.data);
        setSnapshots(snapshotsRes.data.data);
        setTotalRows(snapshotsRes.data.meta.totalItems);
      } catch (error) {
        console.error('Error fetching dashboard snapshots:', error);
      } finally {
        setLoading(false);
      }
    };
    const timer = setTimeout(() => {
      fetchSnapshots();
    }, 300);
    return () => clearTimeout(timer);
  }, [currentPage, rowsPerPage, searchTerm, filterState, filterStatus, filterType, data]);
  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const val = parseInt(pageInput);
    if (!isNaN(val) && val >= 1 && val <= totalPages) {
      setCurrentPage(val);
    } else {
      setPageInput(currentPage.toString());
    }
  };

  if (loading && !data) return <div className="content">Loading company details...</div>;
  if (!data) return <div className="content">Error loading data</div>;
  const {
    stats,
    filingsByState,
    changesByType,
    recentChanges
  } = data;
  const filteredRecentChanges = searchTerm ? recentChanges.filter(c => c.company_name.toLowerCase().includes(searchTerm.toLowerCase())) : recentChanges;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const states = ['All states', ...new Set(filingsByState.map(s => s.state))].filter(Boolean);
  const statuses = ['All statuses', 'Open', 'Approved', 'Rejected', 'Objection'];
  const filingTypes = ['All types', 'Rate/Rule', 'Form', 'Application'];

  const handleExport = async () => {
    // Fetch ALL snapshots for this company, not just the current page
    try {
      const res = await getSnapshots({
        search: searchTerm,
        limit: 10000 // Large limit to ensure we get all records for this organization
      });
      const allSnapshots = res.data.data;
      if (!allSnapshots || allSnapshots.length === 0) return;
      const headers = ['Tracking #', 'Company', 'State', 'Filing Type', 'Status', 'SERFF Status', 'Submission', 'Disposition'];
      const rows = allSnapshots.map(s => [s.serff_tracking_number, s.company_name, s.state, s.filing_type, s.filing_status, s.serff_status || '', s.submission_date || '', s.disposition_date || '']);
      const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;'
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `filings_${searchTerm.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data. Please try again.');
    }
  };
  const getStatusColor = status => {
    if (!status) return 'pill-outline';
    const s = status.toLowerCase();
    if (s.includes('rejected') || s.includes('disapproved') || s.includes('withdrawn')) return 'pill-red';
    if (s.includes('approved') || s.includes('closed') || s.includes('accepted')) return 'pill-green';
    if (s.includes('objection') || s.includes('info requested') || s.includes('question')) return 'pill-amber';
    if (s.includes('assigned') || s.includes('review') || s.includes('submitted')) return 'pill-blue';
    return 'pill-outline';
  };
  const stateAbbreviations = {
    'Alabama': 'AL',
    'Alaska': 'AK',
    'Arizona': 'AZ',
    'Arkansas': 'AR',
    'California': 'CA',
    'Colorado': 'CO',
    'Connecticut': 'CT',
    'Delaware': 'DE',
    'Florida': 'FL',
    'Georgia': 'GA',
    'Hawaii': 'HI',
    'Idaho': 'ID',
    'Illinois': 'IL',
    'Indiana': 'IN',
    'Iowa': 'IA',
    'Kansas': 'KS',
    'Kentucky': 'KY',
    'Louisiana': 'LA',
    'Maine': 'ME',
    'Maryland': 'MD',
    'Massachusetts': 'MA',
    'Michigan': 'MI',
    'Minnesota': 'MN',
    'Mississippi': 'MS',
    'Missouri': 'MO',
    'Montana': 'MT',
    'Nebraska': 'NE',
    'Nevada': 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    'Ohio': 'OH',
    'Oklahoma': 'OK',
    'Oregon': 'OR',
    'Pennsylvania': 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    'Tennessee': 'TN',
    'Texas': 'TX',
    'Utah': 'UT',
    'Vermont': 'VT',
    'Virginia': 'VA',
    'Washington': 'WA',
    'West Virginia': 'WV',
    'Wisconsin': 'WI',
    'Wyoming': 'WY'
  };
  const getHeatColor = (count, view) => {
    if (count === 0) return "var(--c-white)";

    // Total Filings: Yellow/Amber
    if (view === 'total') {
      if (count >= 50) return "var(--c-neon)"; // Dark yellow
      if (count >= 20) return "var(--c-neon)";
      if (count >= 10) return "var(--c-neon)";
      if (count >= 5) return "var(--c-neon)";
      return "var(--c-neon)";
    }

    // Open Filings: Red
    if (view === 'open') {
      if (count >= 20) return "var(--c-black)"; // Dark red
      if (count >= 10) return "var(--text2)";
      if (count >= 5) return "var(--text2)";
      if (count >= 2) return "var(--text3)";
      return "var(--text3)";
    }

    // Total Changes: Orange
    if (view === 'changes') {
      if (count >= 20) return "var(--text2)"; // Dark orange
      if (count >= 10) return "var(--text2)";
      if (count >= 5) return "var(--text3)";
      if (count >= 2) return "var(--text3)";
      return "var(--text3)";
    }

    // Total Objections — gray heat ramp (tokens.css)
    if (view === 'objections') {
      if (count >= 10) return "var(--c-black)";
      if (count >= 5) return "var(--text2)";
      if (count >= 3) return "var(--ink-subtle)";
      if (count >= 2) return "var(--ink-faint)";
      return "var(--text2)";
    }

    // Specific Change Types: Orange (using same logic as 'changes')
    if (view && !['total', 'open', 'changes', 'objections'].includes(view)) {
      if (count >= 10) return "var(--text2)"; // Dark orange
      if (count >= 5) return "var(--text2)";
      if (count >= 3) return "var(--text3)";
      if (count >= 2) return "var(--text3)";
      return "var(--text3)";
    }
    return "var(--border)"; // Default blue
  };
  const getMapData = () => {
    const dataByState = {};

    // Initialize all states with 0 to ensure they show up in the rankings
    Object.values(stateAbbreviations).forEach(code => {
      dataByState[code] = 0;
    });
    if (mapView === 'total') {
      snapshots.forEach(s => {
        if (s.state) dataByState[s.state] = (dataByState[s.state] || 0) + 1;
      });
    } else if (mapView === 'open') {
      filingsByState.forEach(s => {
        dataByState[s.state] = s.count;
      });
    } else if (mapView === 'changes') {
      recentChanges.forEach(c => {
        if (c.state) dataByState[c.state] = (dataByState[c.state] || 0) + 1;
      });
    } else if (mapView === 'objections') {
      snapshots.forEach(s => {
        if (s.state && s.status && s.status.toLowerCase().includes('objection')) {
          dataByState[s.state] = (dataByState[s.state] || 0) + 1;
        }
      });
    } else if (!['total', 'open', 'changes', 'objections'].includes(mapView)) {
      // Dynamic change type
      recentChanges.forEach(c => {
        if (c.state && c.change_type === mapView) {
          dataByState[c.state] = (dataByState[c.state] || 0) + 1;
        }
      });
    }
    return dataByState;
  };
  const currentMapData = getMapData();
  const mapEntries = Object.entries(currentMapData).map(([state, count]) => ({
    state,
    count
  })).sort((a, b) => b.count - a.count);
  return <>
    <div className="topbar">
      <div className="topbar-left">
        <div className="companydetails-style-1">
          <div className="back-btn" onClick={() => navigate('/all-companies')}>
            <ArrowLeft size={18} />
          </div>
          <div className="topbar-title">Company Details</div>
        </div>
        <div className="topbar-sub companydetails-style-2">
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
      </div>
      {/* <div className="topbar-right">
          <span className="chip">
            {filingsByState.map(f => f.state).join(' · ')}
          </span>
          <button className="btn btn-export" onClick={handleExport}>Export CSV</button>
          <button className="btn btn-primary">Run Pass 2</button>
        </div> */}
    </div>

    <div className="content">
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total filings</div>
          <div className="stat-value">{stats.totalFilings}</div>
          <div className="stat-sub">across {filingsByState.length} states</div>
          <div className="stat-delta up">+12 this week</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open filings</div>
          <div className="stat-value">{stats.openFilings}</div>
          <div className="stat-sub">{stats.totalFilings - stats.openFilings} closed</div>
          <div className="stat-delta down">3 respond by tomorrow</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Changes today</div>
          <div className="stat-value">{stats.changesToday}</div>
          <div className="stat-sub">across recent events</div>
          <div className="stat-delta up">Pass 2 complete</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active objections</div>
          <div className="stat-value">{stats.activeObjections}</div>
          <div className="stat-sub">Requiring attention</div>
          <div className="stat-delta down">Check deadlines</div>
        </div>
      </div>

      {/* Two col */}
      <div className="two-col">
        {/* Change log */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">Change log — {searchTerm ? 'Company' : 'recent'}</div>
            <div className="panel-actions">
              <span className="pill pill-blue">{filteredRecentChanges.length} events</span>
              <button className="btn btn-sm companydetails-style-3">View all</button>
            </div>
          </div>
          <div className="scrollable-changes">
            {filteredRecentChanges.map(event => <div className="change-item" key={event.id}>
              <div className={`change-dot dot-${event.change_type.toLowerCase().includes('approved') ? 'approved' : event.change_type.toLowerCase().includes('rejected') ? 'rejected' : event.change_type.toLowerCase().includes('objection') ? 'objection' : 'status'}`}></div>
              <div className="change-body">
                <div className="change-tn">{event.serff_tracking_number}</div>
                <div className="change-company">{event.company_name}</div>
                <div className="change-detail">
                  {event.old_value} → {event.new_value}
                </div>
              </div>
              <div className="change-time">
                {new Date(event.detected_at).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </div>
            </div>)}
          </div>
        </div>

        <div className="companydetails-style-4">
          {/* Open by state */}
          <div className="panel">
            <div className="panel-header companydetails-style-5">
              <div className="companydetails-style-6">
                <div className="panel-title">{mapView.charAt(0).toUpperCase() + mapView.slice(1)} by state</div>
                <span className="pill pill-outline">{mapEntries.length} active states</span>
              </div>
              <div className="btn-group companydetails-style-7">
                <button className={`btn btn-sm ${mapView === 'total' ? 'btn-primary' : ''} companydetails-style-8`} onClick={() => setMapView('total')}>
                  Total Filings
                </button>
                <button className={`btn btn-sm ${mapView === 'open' ? 'btn-primary' : ''} companydetails-style-9`} onClick={() => setMapView('open')}>
                  Open Filings
                </button>
                <button className={`btn btn-sm ${mapView === 'changes' ? 'btn-primary' : ''} companydetails-style-10`} onClick={() => setMapView('changes')}>
                  Total Changes
                </button>
                <button className={`btn btn-sm ${mapView === 'objections' ? 'btn-primary' : ''} companydetails-style-11`} onClick={() => setMapView('objections')}>
                  Total Objections
                </button>
                {changesByType.map(type => <button key={type.change_type} className={`btn btn-sm ${mapView === type.change_type ? 'btn-primary' : ''} companydetails-style-12`} onClick={() => setMapView(type.change_type)}>
                  {type.change_type}
                </button>)}
              </div>
            </div>
            <div className="panel-body companydetails-style-13">
              <div className="map-side companydetails-style-14">
                <div className="map-container">
                  <ComposableMap projection="geoAlbersUsa">
                    <Geographies geography="https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json">
                      {({
                        geographies
                      }) => geographies.map(geo => {
                        const stateName = geo.properties.name;
                        const stateCode = stateAbbreviations[stateName];
                        const count = currentMapData[stateCode] || 0;
                        return <Geography key={geo.rsmKey} geography={geo} fill={getHeatColor(count, mapView)} stroke="var(--text2)" strokeWidth={0.7} style={{
                          default: {
                            outline: "none"
                          },
                          hover: {
                            fill: count > 0 ? "var(--c-blue-1)" : "var(--border2)",
                            outline: "none"
                          },
                          pressed: {
                            outline: "none"
                          }
                        }}>
                          <title>{`${stateName}: ${count} ${mapView}`}</title>
                        </Geography>;
                      })}
                    </Geographies>
                  </ComposableMap>
                </div>
              </div>

              <div className="chart-side companydetails-style-15">
                <div className="side-label companydetails-style-16">Rankings by state</div>
                <div className="bar-chart scrollable-chart companydetails-style-17" key={mapView}>
                  {mapEntries.map(state => <div className="bar-row companydetails-style-18" key={state.state}>
                    <div className="bar-label companydetails-style-19">{state.state}</div>
                    <div className="bar-track companydetails-style-20">
                      <div className="bar-fill" style={{
                        width: `${mapEntries[0].count > 0 ? state.count / mapEntries[0].count * 100 : 0}%`,
                        background: getHeatColor(state.count, mapView)
                      }}></div>
                    </div>
                    <div className="bar-value companydetails-style-21">{state.count}</div>
                  </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filings table */}
      <div className="panel">
        <div className="panel-header companydetails-style-22">
          <div className="panel-title">Filings</div>
          <div className="companydetails-style-23">
            <div className={`select-wrap ${openFilter === 'state' ? 'is-open' : ''}`}>
              <span className="select-measure">{filterState}</span>
              <select className="filter-select" value={filterState} onFocus={() => {
                filterClickCount.current = 0;
                setOpenFilter('state');
              }} onBlur={() => {
                filterClickCount.current = 0;
                setOpenFilter(null);
              }} onClick={e => {
                filterClickCount.current++;
                if (filterClickCount.current > 1) {
                  e.target.blur();
                }
              }} onChange={e => {
                setFilterState(e.target.value);
                setCurrentPage(1);
                e.target.blur();
              }}>
                {states.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className={`select-wrap ${openFilter === 'status' ? 'is-open' : ''}`}>
              <span className="select-measure">{filterStatus}</span>
              <select className="filter-select" value={filterStatus} onFocus={() => {
                filterClickCount.current = 0;
                setOpenFilter('status');
              }} onBlur={() => {
                filterClickCount.current = 0;
                setOpenFilter(null);
              }} onClick={e => {
                filterClickCount.current++;
                if (filterClickCount.current > 1) {
                  e.target.blur();
                }
              }} onChange={e => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
                e.target.blur();
              }}>
                {statuses.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className={`select-wrap ${openFilter === 'type' ? 'is-open' : ''}`}>
              <span className="select-measure">{filterType}</span>
              <select className="filter-select" value={filterType} onFocus={() => {
                filterClickCount.current = 0;
                setOpenFilter('type');
              }} onBlur={() => {
                filterClickCount.current = 0;
                setOpenFilter(null);
              }} onClick={e => {
                filterClickCount.current++;
                if (filterClickCount.current > 1) {
                  e.target.blur();
                }
              }} onChange={e => {
                setFilterType(e.target.value);
                setCurrentPage(1);
                e.target.blur();
              }}>
                {filingTypes.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="panel-actions">
            <button className="btn btn-export" onClick={handleExport}>Export CSV</button>
          </div>
        </div>
        <div className="table-wrap-inline">
          <table>
            <thead>
              <tr>
                <th className="col-tn">Tracking #</th>
                <th className="col-company">Company</th>
                <th className="col-state">State</th>
                <th className="col-type">Filing type</th>
                <th className="col-status">Status</th>
                <th className="col-status">SERFF Status</th>
                <th className="col-date">Submission</th>
                <th className="col-date">Disposition</th>
                <th className="col-count">Corr.</th>
                <th className="col-count">Docs</th>
                <th className="col-zip">Zip</th>
                <th className="companydetails-style-24">Action</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(s => <tr key={s.serff_tracking_number}>
                <td><span className="tn">{s.serff_tracking_number}</span></td>
                <td className="company">{s.company_name}</td>
                <td><span className="pill pill-outline">{s.state}</span></td>
                <td><span className="pill pill-outline">{s.filing_type}</span></td>
                <td><span className={`pill ${getStatusColor(s.filing_status)}`}>{s.filing_status}</span></td>
                <td><span className={`pill ${getStatusColor(s.serff_status)}`}>{s.serff_status || '—'}</span></td>
                <td>{s.submission_date || '—'}</td>
                <td>{s.disposition_date || '—'}</td>
                <td>{s.correspondence_count || 0}</td>
                <td>{Object.keys(s.document_hashes || {}).length}</td>
                <td>{s.last_zip_path ? <span className="zip-yes">Yes</span> : <span className="zip-no">No</span>}</td>
                <td>
                  <button className="btn btn-sm companydetails-style-25" onClick={() => navigate(`/filings?search=${s.serff_tracking_number}`)}>
                    View Filing
                  </button>
                </td>
              </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="pagination">
          <div className="pg-info">
            Showing {totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalRows)} of {totalRows}
          </div>
          <div className="companydetails-style-26">
            <div className="companydetails-style-27">
              <span className="companydetails-style-28">Rows</span>
              <div className={`select-wrap ${openFilter === 'rows' ? 'is-open' : ''}`}>
                <span className="select-measure companydetails-style-29">{rowsPerPage}</span>
                <select className="filter-select companydetails-style-30" value={rowsPerPage} onFocus={() => {
                  filterClickCount.current = 0;
                  setOpenFilter('rows');
                }} onBlur={() => {
                  filterClickCount.current = 0;
                  setOpenFilter(null);
                }} onClick={e => {
                  filterClickCount.current++;
                  if (filterClickCount.current > 1) {
                    e.target.blur();
                  }
                }} onChange={e => {
                  setRowsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                  e.target.blur();
                }}>
                  <option value={10} className="companydetails-style-31">10</option>
                  <option value={25} className="companydetails-style-32">25</option>
                </select>
              </div>
            </div>
            <div className="pg-controls">
              <button className="pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                <ChevronLeft size={14} />
              </button>

              <form onSubmit={handlePageInputSubmit} className="companydetails-style-33">
                <input
                  type="text"
                  className="pg-input"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onBlur={() => setPageInput(currentPage.toString())}
                />
                <button type="submit" style={{ display: 'none' }} />
              </form>

              <button className="pg-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
};
export default CompanyDetails;
